import type { VisualComponent } from '@dtwin/shared';
import { chartOptionFactory } from '../components/charts/chartOptionFactory';

// ============================================
// 导出真实 HTML（自包含单文件，离线可打开）
// ============================================

function esc(s: unknown): string {
  return JSON.stringify(String(s ?? ''));
}

/** 根据控件配置生成 ECharts option（A-03：委托 chartOptionFactory，导出与在线渲染同源） */
export function buildOption(type: string, config: Record<string, unknown>, data: unknown): any {
  return chartOptionFactory(type, config, data);
}

/** 生成自包含 HTML 导出字符串（D-06：可选水印层） */
export function generateHtml(
  name: string,
  width: number,
  height: number,
  bgColor: string,
  widgets: VisualComponent[],
  watermark?: { enabled: boolean; logoUrl: string; text: string; opacity: number } | null,
): string {
  const widgetScripts = widgets
    .filter((w) => buildOption(w.type, w.config || {}, w.dataSource))
    .map((w, idx) => {
      const data = w.dataSource?.type === 'static' ? (w.dataSource.config as any)?.data ?? [] : [];
      const option = buildOption(w.type, w.config || {}, data);
      if (!option) return '';
      const cfg = w.config || {};
      const bg = cfg.bgColor || 'transparent';
      return `
  <div id="w${idx}" style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;border-radius:${cfg.borderRadius || 4}px;background:${bg};"></div>
  <script>
    (function(){
      var el = document.getElementById('w${idx}');
      var chart = echarts.init(el);
      chart.setOption(${JSON.stringify(option)});
    })();
  </script>`;
    })
    .join('\n');

  const staticWidgets = widgets
    .filter((w) => !buildOption(w.type, w.config || {}, w.dataSource))
    .map((w) => {
      const cfg = w.config || {};
      if (w.type === 'text') {
        return `<div style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;color:${cfg.color || '#fff'};font-size:${cfg.fontSize || 16}px;display:flex;align-items:center;justify-content:center;">${esc(cfg.content || '')}</div>`;
      }
      if (w.type === 'image') {
        return `<div style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;overflow:hidden;"><img src="${cfg.src || ''}" style="width:100%;height:100%;object-fit:${cfg.fit || 'contain'};"/></div>`;
      }
      if (w.type === 'counter' || w.type === 'indicator') {
        const data = (w.dataSource?.type === 'static' ? (w.dataSource.config as any)?.data ?? [] : []) as any[];
        const val = data[0]?.value ?? 0;
        return `<div style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;display:flex;flex-direction:column;align-items:center;justify-content:center;color:${cfg.textColor || '#fff'};font-size:${cfg.textFontSize || 36}px;font-weight:700;">${cfg.prefix || ''}${Number(val).toLocaleString()}${cfg.suffix || ''}</div>`;
      }
      if (w.type === 'table') {
        const data = (w.dataSource?.type === 'static' ? (w.dataSource.config as any)?.data ?? [] : []) as any[];
        const cols = data.length ? Object.keys(data[0]) : ['序号', '名称', '状态'];
        const head = cols.map((c) => `<th style="padding:8px;color:#1890ff;background:rgba(24,144,255,0.08);border-bottom:2px solid #1890ff;">${esc(c)}</th>`).join('');
        const body = (data.slice(0, 10).map((r) => `<tr>${cols.map((c) => `<td style="padding:8px;color:#fff;border-bottom:1px solid rgba(255,255,255,0.05);">${esc(r[c])}</td>`).join('')}</tr>`)).join('') || '<tr><td style="padding:8px;color:#888;">暂无数据</td></tr>';
        return `<div style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;overflow:auto;"><table style="width:100%;border-collapse:collapse;font-size:13px;">${head}${body}</table></div>`;
      }
      return `<div style="position:absolute;left:${w.position.x}px;top:${w.position.y}px;width:${w.size.width}px;height:${w.size.height}px;display:flex;align-items:center;justify-content:center;color:#888;font-size:12px;">${esc(w.type)}</div>`;
    })
    .join('\n');

  const wmLayer = watermark?.enabled
    ? `<div style="position:absolute;left:16px;bottom:12px;z-index:9999;pointer-events:none;opacity:${watermark.opacity};display:flex;align-items:center;gap:8px;user-select:none;"><img src="${watermark.logoUrl}" style="height:22px;border-radius:4px;opacity:0.9;"/><span style="color:#fff;font-size:13px;letter-spacing:1px;">${watermark.text}</span></div>`
    : '';

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8">
<title>${esc(name)}</title>
<script src="https://cdn.jsdelivr.net/npm/echarts@6.1.0/dist/echarts.min.js"></script>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:#0a0a1a;overflow:hidden}
.screen{position:relative;background:${bgColor};width:${width}px;height:${height}px;transform-origin:0 0;overflow:hidden}
@media screen{
  .screen{transform:scale(calc(100vw / ${width}), calc(100vh / ${height}))}
}
</style>
</head>
<body>
<div class="screen" id="screen">
${widgetScripts}
${staticWidgets}
${wmLayer}
</div>
<script>
(function(){
  var s = document.getElementById('screen');
  function fit(){
    // 画布完全填满屏幕（scaleX/scaleY 独立拉伸），组件贴边、无空隙、不越界
    var sw = screen.width || window.innerWidth;
    var sh = screen.height || window.innerHeight;
    s.style.transform = 'scale(' + (sw / ${width}) + ', ' + (sh / ${height}) + ')';
  }
  window.addEventListener('resize', fit);
  fit();
})();
</script>
</body>
</html>`;
}
